/**
 * Access-token refresh
 *
 * Single source of truth for refreshing the JWT access token, shared by the
 * axios interceptor (api-client.ts) and the realtime client
 * (useAblyRealtimeClient.ts).
 *
 * Concurrent callers are deduplicated via a shared in-flight promise. This
 * matters because the backend ROTATES the refresh token on every refresh: two
 * concurrent refreshes would each POST the same (now single-use) refresh token,
 * the second would fail, and the user would be logged out. With the shared
 * promise, concurrent callers await one refresh; sequential callers each use the
 * latest stored refresh token.
 */

import axios from "axios"
import { getDeviceId, getRefreshToken, setTokens } from "./token-manager"

const API_URL = import.meta.env.VITE_API_URL

let inFlight: Promise<string> | null = null
let lastRefresh: { token: string; at: number } | null = null
// Bumped whenever the session is torn down (logout). A refresh that completes
// *after* the epoch changed is discarded rather than re-writing the just-cleared
// tokens / re-arming the cooldown — otherwise an in-flight refresh could
// silently resurrect the previous session (an account-bleed risk on shared
// devices).
let authEpoch = 0

// Reuse a just-completed refresh within this window instead of starting a new
// one. The shared `inFlight` promise only dedupes *concurrent* refreshes; a
// staggered burst of 401s (a page-load wave of requests, or repeated Ably
// reconnects) whose responses trickle in after the previous refresh already
// resolved would each kick off another refresh. Because the backend rotates the
// refresh token on every refresh, that churns tokens and can cascade into a
// refresh storm. An access token obtained seconds ago is still valid (they live
// minutes), so returning it lets those requests retry successfully without
// re-refreshing; a non-expiry (authz) 401 is still stopped by the caller's
// `_retry` guard.
export const REFRESH_COOLDOWN_MS = 10_000

/**
 * Refresh the access token using the stored refresh token. Returns the new
 * access token. Throws if there is no refresh token or the refresh request
 * fails (caller should treat that as a terminal auth failure -> log out / stop).
 */
export async function refreshAccessToken(): Promise<string> {
  if (inFlight) return inFlight

  if (lastRefresh && Date.now() - lastRefresh.at < REFRESH_COOLDOWN_MS) {
    return lastRefresh.token
  }

  const startEpoch = authEpoch
  inFlight = (async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      throw new Error("No refresh token available")
    }
    const deviceId = getDeviceId()
    const response = await axios.post(`${API_URL}/auth/refresh-token`, {
      refreshToken,
      deviceId,
    })
    // The session was torn down (logout) while this refresh was in flight.
    // Discard the result instead of rewriting the cleared tokens or re-arming
    // the cooldown with a now-dead session's access token.
    if (authEpoch !== startEpoch) {
      throw new Error("Auth session changed during refresh")
    }
    const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens
    setTokens({
      accessToken,
      refreshToken: newRefreshToken,
      deviceId: deviceId || "",
    })
    lastRefresh = { token: accessToken, at: Date.now() }
    return accessToken
  })()

  try {
    return await inFlight
  } finally {
    inFlight = null
  }
}

/**
 * Tear down refresh state on logout: bump the epoch so any in-flight refresh is
 * discarded when it resolves, and clear the post-refresh cooldown cache so a
 * previous session's token can't be handed back in the next one.
 */
export function resetRefreshCooldown(): void {
  authEpoch++
  lastRefresh = null
}
